<template>
  <div class="my-2">
    <FloatLabel variant="on">
      <Select
        v-model="selectedValue"
        :options="mappedOptions"
        :show-clear="showClear"
        option-label="text"
        option-value="id"
        size="large"
        :disabled="disabled || loading" 
        :loading="loading"
        :invalid="!!error"
        :filter="useFilter"
        class="w-100"
        @change="onChange"
        @filter="onFilter"
      >
        <template #empty>
          <div>
            گزینه‌ای وجود ندارد
          </div>
        </template>
        
        <template #emptyfilter>
          <div>
            نتیجه‌ای یافت نشد
          </div>
        </template>
      </Select>
      <label>{{ placeholder }}</label>
    </FloatLabel>
    
    <small
      v-if="error"
      class="font-size-sm text-red mt-1 d-block"
    >
      {{ error }}
    </small>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import Select from 'primevue/select'
import FloatLabel from 'primevue/floatlabel'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  placeholder: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showClear:{
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: null,
  },
  options: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  }
})

const emit = defineEmits(['update:modelValue', 'onChange'])

// token used to represent real null inside PrimeVue select
const NULL_TOKEN = '__SOHA_NULL__'

// mappedOptions: replace id === null with a token so PrimeVue doesn't treat it as "no value"
const mappedOptions = computed(() => {
  return (props.options || []).map(o => {
    if (o && o.id === null) {
      return { ...o, id: NULL_TOKEN }
    }
    return o
  })
})

// helper: convert incoming value to the same type as option.id when possible
const normalizeValue = (val) => {
  // if parent explicitly sent null and we have a mapped option for null, use the token
  if (val === null) {
    const hasNull = mappedOptions.value.some(o => o.id === NULL_TOKEN)
    return hasNull ? NULL_TOKEN : null
  }
  if (!props.options || props.options.length === 0) return val

  // try strict match against original options (note: mappedOptions uses token)
  let found = props.options.find(o => o.id === val)
  if (found) return found.id === null ? NULL_TOKEN : found.id

  // numeric coercion
  const num = Number(val)
  if (!Number.isNaN(num)) {
    found = props.options.find(o => o.id === num)
    if (found) return found.id === null ? NULL_TOKEN : found.id
  }

  // string coercion
  const str = String(val)
  found = props.options.find(o => String(o.id) === str)
  if (found) return found.id === null ? NULL_TOKEN : found.id

  return val
}

const selectedValue = ref(null)

// Computed
const useFilter = computed(() => {
  return (mappedOptions.value || []).length > 5
})

// Watchers
watch(() => props.modelValue, (newVal) => {
  selectedValue.value = normalizeValue(newVal)
}, { immediate: true })

watch(selectedValue, (newVal) => {
  // map token back to actual null when emitting to parent
  if (newVal === NULL_TOKEN) {
    emit('update:modelValue', null)
  } else {
    emit('update:modelValue', newVal)
  }
})

watch(() => props.options, (newVal) => {
  if (!newVal || newVal.length === 0) return
  selectedValue.value = normalizeValue(props.modelValue)
}, { deep: true, immediate: true })

// Methods
const onChange = (event) => {
  // event.value may be token; find original option in props.options
  const selectedOption = props.options.find(option => {
    if (option && option.id === null && event.value === NULL_TOKEN) return true
    return option && option.id == event.value
  }) || null

  // emit original option object (with null id when appropriate) or null
  emit('onChange', selectedOption || null)
}

const onFilter = () => {
  // فیلتر داخلی PrimeVue به طور خودکار اعمال می‌شود
}
</script>

